// src/components/vocab/VocabResultsScreen.jsx
import React, { useState, useMemo } from 'react';
import { Zap, RotateCcw, Home } from 'lucide-react';
import Mascot from '../Mascot';
import { getMascotDialogue } from '../../data/mascotDialogues';

const getBg = (pct) =>
  pct === 100 ? 'from-yellow-400 to-orange-400'
  : pct >= 70  ? 'from-indigo-500 to-purple-600'
  : pct >= 40  ? 'from-blue-400 to-indigo-500'
  :              'from-slate-400 to-slate-600';

export default function VocabResultsScreen({
  results = [],
  score,
  total,
  xpGained,
  onExit,
  onRetry,
  activeSkin = 'bribark',    // ← viene del padre
  isLegendary = false,       // ← opcional, si manejas legendarias
}) {
  const [tab, setTab] = useState('wrong');

  const pct     = total > 0 ? Math.round((score / total) * 100) : 0;
  const wrong   = results.filter(r => !r.correct);
  const correct = results.filter(r => r.correct);
  const isPassed = pct >= 70;

  // ✅ Usa el sistema real de diálogos
  const situation = pct === 100
    ? 'vocab_challenge_perfect'
    : isPassed
    ? 'vocab_challenge_win'
    : 'vocab_challenge_retry';

  const mascotMessage = useMemo(() =>
    getMascotDialogue(situation, activeSkin, isLegendary),
    [situation, activeSkin, isLegendary]
  );

  return (
    <div className="h-screen max-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex flex-col overflow-hidden">

      {/* ── ZONA FIJA SUPERIOR ──────────────────────── */}
      <div className="shrink-0 p-4 pt-6 space-y-3">

        {/* Tarjeta principal */}
        <div className={`bg-gradient-to-br ${getBg(pct)} rounded-3xl p-5 w-full text-white shadow-2xl`}>

          {/* Stats */}
          <div className="flex justify-center gap-8 mb-4">
            <div className="text-center">
              <div className="text-3xl font-black">{score}/{total}</div>
              <div className="text-white/70 text-[10px] uppercase tracking-widest mt-1">Correctas</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black">{pct}%</div>
              <div className="text-white/70 text-[10px] uppercase tracking-widest mt-1">Precisión</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black flex items-center justify-center gap-1">
                <Zap fill="currentColor" className="text-yellow-300" size={20} />
                {xpGained}
              </div>
              <div className="text-white/70 text-[10px] uppercase tracking-widest mt-1">XP ganado</div>
            </div>
          </div>

          {/* ✅ Mascota real con sistema de diálogos */}
          <div className="flex justify-center">
            <Mascot
              type={activeSkin}
              emotion={isPassed ? 'happy' : 'sad'}
              message={mascotMessage}
              isJumping={pct === 100}
            />
          </div>
        </div>

        {/* Tabs */}
        {results.length > 0 && (
          <div className="flex bg-white rounded-2xl border-2 border-indigo-50 shadow-sm overflow-hidden">
            {[
              { key: 'wrong',   label: `❌ Falladas`,  count: wrong.length   },
              { key: 'correct', label: `✅ Correctas`, count: correct.length },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 py-3 text-xs font-black transition-all
                  ${tab === t.key
                    ? 'text-indigo-600 bg-indigo-50 border-b-2 border-indigo-500'
                    : 'text-slate-400 hover:text-slate-600'
                  }`}
              >
                {t.label} ({t.count})
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── ZONA SCROLLEABLE ────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 pb-2 space-y-2">

        {tab === 'wrong' && (
          wrong.length === 0
            ? <p className="text-sm text-center text-slate-400 py-6">🎉 ¡No fallaste ninguna!</p>
            : wrong.map((r, i) => (
                <div key={i} className="bg-red-50 rounded-2xl px-4 py-3 border border-red-100">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-black text-slate-800">{r.word?.en}</p>
                      {r.word?.ipa && (
                        <p className="text-[10px] font-mono text-slate-400">{r.word.ipa}</p>
                      )}
                    </div>
                    <div className="text-right space-y-0.5">
                      {r.selected
                        ? <p className="text-xs font-bold text-red-400 line-through">{r.selected}</p>
                        : <p className="text-xs font-bold text-slate-400 italic">Sin respuesta</p>
                      }
                      <p className="text-xs font-black text-emerald-600">✓ {r.word?.es}</p>
                    </div>
                  </div>
                </div>
              ))
        )}

        {tab === 'correct' && (
          correct.length === 0
            ? <p className="text-sm text-center text-slate-400 py-6">Aún no acertaste ninguna.</p>
            : correct.map((r, i) => (
                <div key={i} className="flex items-center justify-between bg-emerald-50 rounded-2xl px-4 py-3 border border-emerald-100">
                  <div>
                    <p className="text-sm font-black text-slate-800">{r.word?.en}</p>
                    {r.word?.ipa && (
                      <p className="text-[10px] font-mono text-slate-400">{r.word.ipa}</p>
                    )}
                  </div>
                  <p className="text-xs font-black text-emerald-600">✓ {r.word?.es}</p>
                </div>
              ))
        )}
      </div>

      {/* ── BOTONES FIJOS ───────────────────────────── */}
      <div className="shrink-0 p-4 space-y-2">
        {onRetry && (
          <button
            onClick={onRetry}
            className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black text-sm shadow-lg shadow-indigo-200 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 border-b-4 border-indigo-800"
          >
            <RotateCcw size={16} />
            INTENTAR DE NUEVO
          </button>
        )}
        <button
          onClick={onExit}
          className="w-full bg-slate-100 text-slate-500 py-3 rounded-2xl font-black text-xs hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
        >
          <Home size={14} />
          VOLVER AL INICIO
        </button>
      </div>

    </div>
  );
}
